import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { px as _px, withVars as _withVars, cssRef as _cssRef, mergeProps as _mergeProps } from "@terraindigital/static-styles-react";
const _staticStyles = _cssRef("input__input-3kyjwa", {
  display: "block",
  border: "var(--static-styles-0js7fdvk_border)",
  "&:not(:placeholder-shown) ~ div label": {
    opacity: "1"
  },
  "&:not(:placeholder-shown)": {
    backgroundColor: "transparent",
    border: "2px solid var(--system-grey-200)"
  },
  padding: "12px",
  width: "100%",
  fontSize: "16px",
  backgroundColor: "var(--system-grey-200)",
  color: "var(--system-black)",
  borderRadius: "4px",
  "&::placeholder": {
    fontSize: "16px",
    color: "var(--system-grey-600)"
  }
});
import React from 'react';
import { FieldError } from 'react-hook-form';
import FormAlert from '../form-alert';
import usePersistedId from '../utils/use-persisted-id';
import throwOnMissing from '../utils/throw-on-missing';
import FormLabel from '../form-label';
import { colors } from '../colors';
import useFormState from '../form-provider/use-form-state';
export interface InputProps extends HtmlPropsNoRef<HTMLInputElement> {
  /**
  * Name of the input - will be used for the form validation if using FormContext so make sure it's unique.
  */
  name: string;
  /**
  * Placeholder is always required.
  * */
  placeholder: string;
  /**
  * The type of the field. Notice that "submit" is not included -
  * This is an intentional omission, you should be using <button type="submit" />
  * instead as there is no need to add a validation layer to a submit field itself.
  * */
  type?: 'email' | 'text' | 'tel' | 'number' | 'password';
  /**
  * Optional validation regex. Requires the form to be in a <FormProvider /> to work.
  * This should only be used if you need custom validation - Generic regex validation for fields based on type
  * (i.e. email validation for email fields, password strength validation for password fields)
  * is already built in.
  * */
  validationRegex?: RegExp;
  /**
  * Define a minimum length for the field.
  * */
  minLength?: number;
  /**
  * Define a maximum length for the field.
  * */
  maxLength?: number;
  /**
  * Optionally add a custom validation error message. Error messages are already built in, and are done based
  * on the type of component and type of error. However, you may add your own custom one. You can use a basic string
  * value here, or pass in a function (which will receive an input argument that includes the type of error -
  * i.e. a Regex validation error and your function will receive 'pattern' as the argument.)
  * */
  validationErrorMessage?: string | ((type: FieldError) => string);
  /**
  * Make the field required or not. Defaults to false for all field types.
  */
  required?: boolean;
  hideLabel?: boolean;
}

/**
 * An `<input />` component that is accessible. If used with `<FormProvider />`,
 * it'll also automatically validate the input based on type, and show clear error messages
 * while linking its value to the FormProvider's onSubmit event.
 * Regexes, min/max length, and messages are built in, but can be overwritten using optional extra props.
 *
 * @alias InputProps
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  type = 'text',
  name,
  validationRegex,
  minLength,
  maxLength,
  validationErrorMessage,
  required = false,
  className,
  value,
  onChange,
  disabled,
  defaultValue,
  hideLabel,
  ...props
}, ref) => {
  throwOnMissing(name, 'name', 'Input');
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore Someone might still do this even though it's not defined in the type
  if (type === 'submit') {
    throw new Error('Primitive Components: The Input Primitive should not be used as a submit button. Please use <button type="submit" /> instead.');
  }
  const typeRules = {
    password: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
    email: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    /* eslint-disable-next-line no-useless-escape */
    tel: /[0-9 \-\(\)]+/g,
    number: /^-?\d+\.?\d*$/,
    text: undefined
  }[type];

  /*
  * Get our form provider. It may not exist
  * (if the input component is not inside a FormProvider and is using the component separately)
  * so make sure to not access it directly without first checking.
  * */
  const formContext = useFormState<string>({
    name,
    value: value as string,
    defaultValue: defaultValue as string,
    validators: {
      required,
      minLength,
      maxLength,
      pattern: validationRegex || typeRules
    },
    disabled
  });
  const id = usePersistedId();
  return <>
            <span className="input__span-2anoeb">
                <input {..._mergeProps({
        ref: ref,
        id: id,
        "aria-invalid": formContext.error ? 'true' : 'false',
        "aria-required": required || undefined,
        className: className,
        type: type,
        css: _withVars(_staticStyles, {
          "--static-styles-0js7fdvk_border": _px(formContext.error ? `2px solid ${colors.statesError}` : '2px solid transparent')
        })
      }, props, {
        value: value !== undefined ? value : formContext?.value ?? '',
        onChange: e => {
          formContext.setValue(e.target.value);
          onChange?.(e);
        }
      })} />
                {props?.placeholder && !hideLabel && <FormLabel id={id}>
                        {props.placeholder}
                    </FormLabel>}
                {required && <span aria-hidden="true" className="input__span-31r48h">
                        <span className="input__span-0mf4fi" style={{
          "--static-styles-19voy9fb_color": _px(formContext.error ? colors.statesError : colors.grey700)
        }}>
                            *
                        </span>
                    </span>}
            </span>
            <div>
                {formContext.error && <FormAlert>{formContext.error}</FormAlert>}
            </div>
        </>;
});
export default Input;